import { useCallback } from 'react';
import { ExplorerItem, ExplorerItems, MutateExplorerItems } from '@/types/types.ts';
import useExplorerItems from '@/common/hooks/useExplorerItems.ts';
import { jotaiStore } from '@/state/jotai/jotaiStore.ts';
import { editingSegmentAtom } from '@/state/jotai/atom/editingSegmentAtom.ts';
import {
  deleteSegmentFromPeaks, disablePeaksEditSegmentMode, updateSegmentInPeaks, upsertSegmentInPeaks,
} from '@/features/Waveform/utils.ts';
import { Segment } from 'peaks.js';
import { recordingSegmentAtom } from '@/state/jotai/atom/recordingSegmentAtom.ts';
import usePlaylists from '@/common/hooks/usePlaylists.ts';
import { ExplorerItemType } from '@/enum/explorerItemType.ts';
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import { PlaymodeType } from "@/enum/playmodeType.ts";
import useSearchResults from "@/common/hooks/useSearchResults.ts";
import { useSearchParamState } from "@/common/hooks/nuqs/useSearchParamState.ts";
import { URLSearchParamKeys } from "@/enum/URLSearchParamKeys.ts";

export function useMutateExplorerItems(): MutateExplorerItems {
  const [uniqueTabId] = useUniqueTabId()
  const [explorerItems, setExplorerItems] = useExplorerItems();
  const [searchResults, setSearchResults] = useSearchResults();
  const [, setPlaylists] = usePlaylists();
  const [, setPlaymode] = useSearchParamState(URLSearchParamKeys.PLAY_MODE);

  const updateItem = useCallback(
    (item: ExplorerItem) => {

      // console.log('useMutateExplorerItems.updateItem', item)

      if (item.type === ExplorerItemType.PLAYLIST) {
        setPlaylists((prevItems: ExplorerItems) => ({
          ...prevItems,
          [item.id]: {
            ...(prevItems?.[item.id] || {}),
            ...item,
          },
        }));
      }

      setExplorerItems((prevItems: ExplorerItems) => {
        const newItem = {
          ...(prevItems?.[item.id] || {}),
          ...item,
        }

        newItem.track = { ...newItem.track }

        return {
          ...prevItems,
          [item.id]: newItem,
        };
      });

      if (searchResults && searchResults?.[item.id]) {

        // console.log('useMutateExplorerItems.setSearchResults')

        setSearchResults((prevResults) => {
          if (!prevResults) return prevResults;

          return {
            ...prevResults,
            [item.id]: {
              ...(prevResults?.[item.id] || {}),
              ...item,
            },
          };
        });
      }
    },
    [
      setExplorerItems,
      searchResults,
      setPlaylists,
      setSearchResults
    ]
  );

  const updateItems = useCallback(
    (items: ExplorerItem[]) => {
      // console.log('useMutateExplorerItems.updateItems');
      /*//
      setPlaylists((prevItems: ExplorerItems) => {
        const updatedPlaylists = { ...prevItems };
        items.forEach((item) => {
          if (item.type === ExplorerItemType.PLAYLIST) {
            updatedPlaylists[item.id] = {
              ...(prevItems?.[item.id] || {}),
              ...item,
            };
          }
        });
        return updatedPlaylists;
      });*/

      setExplorerItems((prevItems: ExplorerItems) => {
        const updatedExplorerItems = { ...prevItems };
        items.forEach((item) => {
          updatedExplorerItems[item.id] = {
            ...(prevItems?.[item.id] || {}),
            ...item,
          };
        });
        return updatedExplorerItems;
      });

      // Werk de zoekresultaten bij, als deze bestaan
      if (searchResults) {
        setSearchResults((prevResults) => {
          if (!prevResults) return prevResults;

          const updatedSearchResults = { ...prevResults };
          items.forEach((item) => {
            if (prevResults[item.id]) {
              updatedSearchResults[item.id] = {
                ...(prevResults?.[item.id] || {}),
                ...item,
              };
            }
          });
          return updatedSearchResults;
        });
      }
    },
    [
      setExplorerItems,
      searchResults,
      setSearchResults
    ]
  );

  const setItems = useCallback(
    (items: ExplorerItems) => {
      setExplorerItems(items);
    },
    [setExplorerItems]
  );

  const prependItems = useCallback(
    (items: ExplorerItems) => {
      // console.log('useMutateExplorerItems.prependItems')

      setExplorerItems((prevItems: ExplorerItems) => {
        return {
          ...items,
          ...prevItems,
        };
      });
    },
    [setExplorerItems]
  );

  const prependItem = useCallback(
    (item: ExplorerItem) => {
      // console.log('useMutateExplorerItems.prependItem')

      if (item.type === ExplorerItemType.PLAYLIST) {
        setPlaylists((prevItems) => ({
          [item.id]: item,
          ...prevItems,
        }));
      }

      setExplorerItems((prevItems) => {
        return {
          [item.id]: item,
          ...prevItems,
        };
      });
    },
    [
      setPlaylists,
      setExplorerItems
    ]
  );

  const appendItem = useCallback(
    (item: ExplorerItem) => {
      // console.log('useMutateExplorerItems.appendItem')

      setExplorerItems((prevItems: ExplorerItems) => {
        const newItems = { ...prevItems };
        newItems[item.id] = item;
        return newItems;
      });
    },
    [setExplorerItems]
  );

  const appendItems = useCallback(
    (items: ExplorerItems) => {
      // console.log('useMutateExplorerItems.appendItems')

      setExplorerItems((prevItems: ExplorerItems) => {
        return {
          ...prevItems,
          ...items,
        };
      });
    },
    [setExplorerItems]
  );

  const removeItem = useCallback(
    (itemId: string) => {
      // console.log('useMutateExplorerItems.removeItem')

      const item = explorerItems?.[itemId];
      if (item?.type === ExplorerItemType.PLAYLIST) {
        setPlaylists((prevItems) => {
          const newItems = { ...prevItems };
          delete newItems[itemId];
          return newItems;
        });
      }

      setExplorerItems((prevItems: ExplorerItems) => {
        const newItems = { ...prevItems };
        delete newItems[itemId];
        return newItems;
      });
    },
    [
      explorerItems,
      setPlaylists,
      setExplorerItems
    ]
  );

  const removeItems = useCallback(
    (itemIds: string[]) => {
      (itemIds || []).forEach((itemId) => {
        removeItem(itemId);
      });
    },
    [removeItem]
  );

  const upsertTrackSegment = useCallback(
    (explorerItemId: string, segment: Segment, fromTabId: string, isRecording?: boolean) => {
      if (isRecording) {
        jotaiStore.set(recordingSegmentAtom, segment);
        setPlaymode(PlaymodeType.PLAYLIST);
        disablePeaksEditSegmentMode();
      }

      setExplorerItems((prevItems: ExplorerItems) => {
        const itemToUpdate = prevItems?.[explorerItemId];
        // console.log(prevItems?.[explorerItemId])

        if (!itemToUpdate) {
          console.log(`Warning: the explorerItem with id ${explorerItemId} was not found @ useMutateExplorerItems.upsertTrackSegment.`)
          return prevItems;
        }

        // Voeg de nieuwe segmenten toe aan de segments van de track
        const updatedSegments = {
          ...itemToUpdate.track.segments,
          [segment.id as string]: segment,
        };

        const track = {
          ...itemToUpdate.track,
          segments: updatedSegments,
        };

        // Maak een nieuw ExplorerItem met de bijgewerkte track
        const updatedItem: ExplorerItem = {
          ...itemToUpdate,
          track,
        };

        // Maak een nieuw object met de bijgewerkte ExplorerItems
        return {
          ...prevItems,
          [explorerItemId]: updatedItem,
        };
      });

      jotaiStore.set(editingSegmentAtom, segment);

      // @note: only for Peaks.js we want to keep the 'edit mode' of a Segment after creation or updates of the segment position / duration
      const newSegment = {
        ...segment,
        editable: uniqueTabId === fromTabId
      }

      upsertSegmentInPeaks(newSegment);
    },
    [
      setExplorerItems,
      setPlaymode,
      uniqueTabId
    ]
  );

  const deleteTrackSegment = useCallback(
    (explorerItemId: string, segment: Segment) => {
      setExplorerItems((prevItems: ExplorerItems) => {
        // Vind het juiste ExplorerItem
        const itemToUpdate = prevItems?.[explorerItemId];
        if (!itemToUpdate) {
          console.log(`Warning: the explorerItem with id ${explorerItemId} was not found @ useMutateExplorerItems.deleteTrackSegment.`)
          return prevItems;
        }

        // Maak een kopie van de huidige segments
        const updatedSegments = { ...itemToUpdate.track.segments };
        // Verwijder het gewenste segment
        delete updatedSegments[segment.id as string];

        // Maak een nieuw ExplorerItem met de bijgewerkte track
        const updatedItem: ExplorerItem = {
          ...itemToUpdate,
          track: {
            ...itemToUpdate.track,
            segments: updatedSegments,
          },
        };

        // Maak een nieuw object met de bijgewerkte ExplorerItems
        return {
          ...prevItems,
          [explorerItemId]: updatedItem,
        };
      });

      const editingSegment = jotaiStore.get(editingSegmentAtom);
      if (editingSegment?.id === segment.id) {
        jotaiStore.set(editingSegmentAtom, null);
      }

      deleteSegmentFromPeaks(segment.id as string);
    },
    [
      setExplorerItems
    ]
  );

  const updateTrackSegment = useCallback(
    (explorerItemId: string, segment: Segment) => {
      setExplorerItems((prevItems: ExplorerItems) => {
        const itemToUpdate = prevItems?.[explorerItemId];
        if (!itemToUpdate) {
          console.log(`Warning: the explorerItem with id ${explorerItemId} was not found @ useMutateExplorerItems.updateTrackSegment.`)
          return prevItems;
        }

        const updatedSegments = {
          ...itemToUpdate.track.segments,
          [segment.id as string]: segment,
        };

        const track = {
          ...itemToUpdate.track,
          segments: updatedSegments,
        };

        const updatedItem: ExplorerItem = {
          ...itemToUpdate,
          track,
        };

        return {
          ...prevItems,
          [explorerItemId]: updatedItem,
        };
      });

      jotaiStore.set(editingSegmentAtom, segment);

      disablePeaksEditSegmentMode();

      updateSegmentInPeaks(segment);
    },
    [setExplorerItems]
  );

  const removePlaylist = useCallback(
    (itemId: string) => {
      setPlaylists((prevItems) => {
        const newItems = { ...prevItems };
        delete newItems[itemId];
        return newItems;
      });
    },
    [setPlaylists]
  );

  const removePlaylists = useCallback(
    (itemIds: string[]) => {
      itemIds.forEach((itemId) => {
        removePlaylist(itemId);
      });
    },
    [removePlaylist]
  );

  return {
    setItems,
    prependItems,
    prependItem,
    updateItem,
    updateItems,
    appendItem,
    appendItems,
    removeItem,
    removeItems,
    removePlaylist,
    removePlaylists,
    upsertTrackSegment,
    updateTrackSegment,
    deleteTrackSegment,
  };
}
